import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ContractStateReason, EcaModalType, ErrorMessage } from './types';

export type ElectronicCustomerAgreement = {
  contractInstance: ContractInstance;
  isLoading: boolean;
  ecaModalType: EcaModalType.Success | EcaModalType.Error | null;
  error: ErrorMessage | null;
  hasSeenSuccessScreen: boolean;
};

export type ContractInstance = {
  contractId: string;
  signedStatus: boolean;
  signerUrl: string;
  dateCreated: string;
  dateModified: string;
  customerId: string;
  productInstanceId: string | null;
  contractState: {
    state: string;
    reason: string;
  };
};

export const initialECAState: ElectronicCustomerAgreement = {
  contractInstance: {
    contractId: '',
    signedStatus: true,
    signerUrl: '',
    dateCreated: '',
    dateModified: '',
    customerId: '',
    productInstanceId: null,
    contractState: {
      state: ContractStateReason.Unsigned,
      reason: '',
    },
  },
  isLoading: false,
  ecaModalType: null,
  error: null,
  hasSeenSuccessScreen: false,
};

export const ecaSlice = createSlice({
  name: 'eca',
  initialState: initialECAState,
  reducers: {
    getContractInstance: (state) => {
      state.isLoading = true;
    },
    setContractInstance: (state, action: PayloadAction<ContractInstance>) => {
      state.contractInstance = action.payload;
    },
    setEcaModalType: (
      state,
      action: PayloadAction<EcaModalType.Success | EcaModalType.Error | null>
    ) => {
      state.ecaModalType = action.payload;
    },
    setEcaIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setEcaError: (state, action: PayloadAction<ErrorMessage | null>) => {
      state.error = action.payload;
    },
    resetContractInstance(state) {
      state.contractInstance = initialECAState.contractInstance;
      state.isLoading = true;
      state.error = null;
      state.ecaModalType = null;
    },
    setHasSeenSuccessScreen: (state, action: PayloadAction<boolean>) => {
      state.hasSeenSuccessScreen = action.payload;
    },
  },
});

export const {
  getContractInstance,
  setContractInstance,
  setEcaModalType,
  setEcaIsLoading,
  setEcaError,
  resetContractInstance,
  setHasSeenSuccessScreen,
} = ecaSlice.actions;

export const selectContractInstance = (state: {
  eca: ElectronicCustomerAgreement;
}) => state.eca.contractInstance;

export const selectIsContractLoading = (state: {
  eca: ElectronicCustomerAgreement;
}) => state.eca.isLoading;

export const selectEcaModalType = (state: {
  eca: ElectronicCustomerAgreement;
}) => state.eca.ecaModalType;

export const selectEcaIsLoading = (state: {
  eca: ElectronicCustomerAgreement;
}) => state.eca.isLoading;

export const selectEcaError = (state: { eca: ElectronicCustomerAgreement }) =>
  state.eca.error;

export const selectHasSeenSuccessScreen = (state: {
  eca: ElectronicCustomerAgreement;
}) => state.eca.hasSeenSuccessScreen;
